// src/components/eframes/EFrame173.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveHeader,
  Card,
} from "../ui";
import { useEFrameData } from "./hooks/useEFrameData";
import { getPlanOwnerName } from "../../utils/planOwnerLabels";
import type { UnifiedContact } from "../../services/unifiedContactService";
import { unifiedContactService } from "../../services/unifiedContactService";
import { documentService } from "../../services/documentService";
import type { AppDocument } from "../../types/document";
import { DocumentsIcon } from "../ui/Icons";
import BackToExecutorDashboardButton from "./BackToExecutorDashboardButton";

interface MemoryCollection {
  personId: string;
  memories: string;
  files: AppDocument[];
}

interface PersonWithMemories extends UnifiedContact {
  hasMemories: boolean;
  memoriesPreview: string;
  fileCount: number;
  files: AppDocument[];
  fullMemories: string;
}

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const EFrame173: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const { session } = useEFrameData(estateId ?? null);
  const planOwnerName = getPlanOwnerName(session.name);

  const [peopleWithMemories, setPeopleWithMemories] = useState<PersonWithMemories[]>([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [totalFileSize, setTotalFileSize] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);

      try {
        const contacts = estateId
          ? await unifiedContactService.getContactsForEstate(estateId)
          : await unifiedContactService.getAllContacts();

        const memoryData = session.taskProgress?.memoryCollections?.answers;
        const rawMemoryCollections = memoryData?.memoryCollections || [];

        const memoryCollections: MemoryCollection[] = rawMemoryCollections.map(
          (collection: MemoryCollection) => ({
            ...collection,
            files: normalizeDocuments(collection.files || []),
          })
        );

        const enrichedPeople: PersonWithMemories[] = contacts
          .map((contact) => {
            const collection = memoryCollections.find(
              (item) => item.personId === contact.id
            );

            const normalizedFiles = normalizeDocuments(collection?.files || []);
            const fullMemories = collection?.memories || "";

            const hasMemories =
              !!collection &&
              (fullMemories.trim().length > 0 || normalizedFiles.length > 0);

            return {
              ...contact,
              hasMemories,
              memoriesPreview: fullMemories
                ? fullMemories.substring(0, 150) +
                  (fullMemories.length > 150 ? "..." : "")
                : "",
              fileCount: normalizedFiles.length,
              files: normalizedFiles,
              fullMemories,
            };
          })
          .filter((person) => person.hasMemories);

        let totalFileCount = 0;
        let totalSize = 0;

        memoryCollections.forEach((collection) => {
          collection.files.forEach((file) => {
            totalFileCount++;
            totalSize += file.size || 0;
          });
        });

        setPeopleWithMemories(enrichedPeople);
        setTotalFiles(totalFileCount);
        setTotalFileSize(totalSize);
      } catch (error) {
        console.error("[EFrame173] Error loading data:", error);
        setPeopleWithMemories([]);
        setTotalFiles(0);
        setTotalFileSize(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [estateId, session.taskProgress?.memoryCollections]);

  const toggleExpandedCard = (personId: string) => {
    setExpandedCards((prev) => ({
      ...prev,
      [personId]: !prev[personId],
    }));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getPersonName = (person: UnifiedContact) => {
    return `${person.firstName} ${person.lastName}`.trim();
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="mb-6 flex justify-start">
          <BackToExecutorDashboardButton />
        </div>
        <ResponsiveHeader
          title={`${planOwnerName}'s Legacy Collection`}
        />

        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin" />
            <p className="text-stone-600 mt-4">
              Loading memory collections...
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-stone-800 mb-4">
                Memory Collection Overview
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-900">
                    {peopleWithMemories.length}
                  </div>
                  <div className="text-sm text-stone-600">
                    People with Memories
                  </div>
                </div>

                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {totalFiles}
                  </div>
                  <div className="text-sm text-stone-600">Memory Files</div>
                </div>

                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {formatFileSize(totalFileSize)}
                  </div>
                  <div className="text-sm text-stone-600">Total Size</div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-stone-800">
                  Memory Collections
                </h3>
              </div>

              {peopleWithMemories.length > 0 ? (
                <div
                  className="space-y-4 overflow-y-auto pr-2 scrollbar-thin"
                  style={{ maxHeight: "400px" }}
                >
                  {peopleWithMemories.map((person) => {
                    const isExpanded = !!expandedCards[person.id];
                    const hasNotes = person.fullMemories.trim().length > 0;
                    const hasFiles = person.files.length > 0;

                    return (
                      <div
                        key={person.id}
                        className="border border-stone-200 rounded-lg p-4"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h4 className="font-medium text-stone-800">
                                  {getPersonName(person)}
                                </h4>

                                {person.relationship && (
                                  <p className="text-sm text-stone-600">
                                    {person.relationship}
                                  </p>
                                )}
                              </div>

                              {person.fileCount > 0 && (
                                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                                  {person.fileCount}{" "}
                                  {person.fileCount === 1 ? "file" : "files"}
                                </span>
                              )}
                            </div>

                            {!isExpanded && person.memoriesPreview && (
                              <div className="mt-3 p-3 bg-stone-50 rounded-md">
                                <p className="text-sm text-stone-700 italic">
                                  "{person.memoriesPreview}"
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2 shrink-0 w-full sm:w-auto">
                            <button
                              type="button"
                              onClick={() => toggleExpandedCard(person.id)}
                              className="text-stone-600 hover:text-stone-800 px-3 py-1 rounded border border-stone-300 hover:border-stone-500 text-sm font-medium whitespace-nowrap transition-colors"
                            >
                              {isExpanded ? "Collapse" : "View Note"}
                            </button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-stone-200 space-y-4">
                            {hasNotes && (
                              <div>
                                <div className="font-sans text-sm font-medium text-stone-700 mb-2">
                                  Notes
                                </div>

                                <div className="text-sm text-stone-600 whitespace-pre-wrap leading-relaxed">
                                  {person.fullMemories}
                                </div>
                              </div>
                            )}

                            {hasFiles && (
                              <div>
                                <div className="font-sans text-sm font-medium text-stone-700 mb-2">
                                  Files
                                </div>

                                <div className="space-y-2">
                                  {person.files.map((file, index) => (
                                    <div
                                      key={`${person.id}-${file.id}-${index}`}
                                      className="flex items-start gap-2 rounded-md border border-stone-200 bg-white px-3 py-2"
                                    >
                                      <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />

                                      <span
                                        className="text-sm font-medium text-stone-800 break-words whitespace-normal"
                                        title={file.name}
                                      >
                                        {file.name}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No memory collections available.</p>
                </div>
              )}
            </Card>
          </div>
        )}
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EFrame173;